import React from 'react';
import { HydratedPost } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { AudioIcon, MicIcon, GoodieWhiteIcon } from './Icons';

interface BumpedPostContentProps {
    post: HydratedPost;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    hasBorder?: boolean;
    onPostClick?: (post: HydratedPost) => void;
}

export const BumpedPostContent = ({
    post,
    onNavigateToProfile,
    onNavigateToHashtag,
    hasBorder = true,
    onPostClick
}: BumpedPostContentProps) => {
    const { usersData } = useAuth();
    const firstMedia = post.content.media?.[0];

    // Fallback for deleted original post
    if (post.type === 'bump' && !post.originalPost) {
        return (
            <div className="flex flex-col h-full">
                <div className="border border-gray-700 rounded-xl overflow-hidden mx-2 mt-4 bg-gray-900 p-2 flex items-center justify-center">
                    <p className="text-center text-lg text-red-400">This bump's original content was deleted.</p>
                </div>
            </div>
        );
    }

    // This is a simplified, non-interactive renderer for the original post inside a bump.
    const renderOriginalContent = () => {
        switch (post.type) {
            case 'image':
                if (post.isArchived && !firstMedia) {
                    return (
                        <div className="flex flex-col items-center justify-center h-full w-full bg-gray-900/50 p-4">
                            <GoodieWhiteIcon className="w-8 h-8 opacity-20 mb-1" />
                            <span className="text-[8px] text-white/30 uppercase tracking-tighter font-bold">Media Removed</span>
                        </div>
                    );
                }
                return firstMedia ? <img src={firstMedia.url} alt={post.content.text || 'Post image'} className="object-cover w-full h-full" loading="lazy" /> : null;
            case 'video':
                if (post.isArchived && !firstMedia) {
                    return (
                        <div className="flex flex-col items-center justify-center h-full w-full bg-gray-900/50 p-4">
                            <AudioIcon className="w-8 h-8 text-white/20 mb-1" />
                            <span className="text-[8px] text-white/30 uppercase tracking-tighter font-bold">Media Removed</span>
                        </div>
                    );
                }
                return firstMedia ? <video src={`${firstMedia.url}#t=0.001`} className="object-cover w-full h-full" loop muted playsInline preload="metadata" /> : null;
            case 'text':
                return <p className="text-md p-4 text-center text-raised">{post.content.text}</p>;
            case 'audio':
            case 'voice':
                return (
                    <div className="p-4 flex flex-col items-center justify-center h-full bg-cover bg-center" style={{ backgroundImage: post.content.coverImageUrl ? `url(${post.content.coverImageUrl})` : 'none' }}>
                        <div className="absolute inset-0 bg-black/60"></div>
                        <div className="relative z-10 flex flex-col items-center justify-center text-center">
                            <div className="text-green-400 mb-2">{post.type === 'audio' ? <AudioIcon className="w-8 h-8" /> : <MicIcon className="w-8 h-8" />}</div>
                            <p className="text-sm line-clamp-2 text-raised">{post.content.text}</p>
                        </div>
                    </div>
                );
            case 'link':
                return (
                    <div className="p-2 flex flex-col justify-between h-full text-left">
                        <p className="text-xs text-raised line-clamp-2">{post.content.text}</p>
                        <div className="p-2 bg-gray-800/50 rounded-lg flex flex-col mt-1">
                            {post.content.linkPreview?.image && <img src={post.content.linkPreview.image} alt="Link preview" />}
                            <h3 className="font-bold text-sm text-raised truncate">{post.content.linkPreview?.title}</h3>
                            <p className="text-xs text-gray-500 mt-1 truncate">{post.content.linkPreview?.domain}</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const containerClass = hasBorder
        ? "border border-gray-700 rounded-xl overflow-hidden mt-3"
        : "rounded-xl overflow-hidden mt-2 flex-grow flex flex-col";

    const contentContainerClass = hasBorder
        ? "relative aspect-video flex items-center justify-center"
        : "relative flex-grow flex items-center justify-center";

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onPostClick) {
            onPostClick(post);
        }
    };

    return (
        <div className={containerClass} onClick={handleClick} style={{ cursor: onPostClick ? 'pointer' : 'default' }}>
            <button onClick={(e) => { e.stopPropagation(); onNavigateToProfile(post.author); }} className="flex items-center gap-2 p-2 bg-gray-800/30 w-full text-left hover:bg-gray-800/50 transition-colors">
                <img src={usersData[post.author]?.pfp} className="w-6 h-6 rounded-full object-cover" alt={post.author} />
                <span className="text-sm font-semibold">{post.author}</span>
            </button>
            <div className={contentContainerClass}>
                {renderOriginalContent()}
            </div>
        </div>
    );
};

